import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { State } from 'redux/initialState';

import { useTranslate } from 'hooks/useTranslate';
import { useGetActualSociety } from 'hooks/useGetActualSociety';

import Page from 'modules/_shared/components/Pages/Page';
import GoBackButton from 'modules/_shared/components/Buttons/GoBackButton';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import PageListHeader from 'modules/_shared/components/Headers/PageListHeader';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { getLastTitleNumber } from 'modules/partners/utils/getLastTitleNumber';

import DataTablePagination from 'modules/_shared/components/Tables/DataTablePagination';
import { useState } from 'react';
import SearchModule from 'modules/_shared/components/SearchModule';
import { formatSearchPlaceholder } from 'modules/_shared/components/Tables/utils/formatSearchPlaceholder';
import SharesTitlePageBody from './components/SharesTitlesPageBody';
import { useSharesTitles } from './hooks/useShareTitles';

function TitleRegistryBook() {
  const { societyId } = useParams();

  const { t, i18n } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const sharesTitles = useSelector((state: State) => state.sharesTitles);

  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  const handleChangeRowsPerPage = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage);
  };

  useGetMenu();
  useGetActualSociety(societyId);

  const useSharesTitlesResult = useSharesTitles({
    societyId,
    page: currentPage,
    items: rowsPerPage,
  });

  const { isLoading } = useSharesTitlesResult;
  const { fetchSharesTitles } = useSharesTitlesResult.actions;

  const handleChangeSearchTerm = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    setSearchTerm(event.target.value);
  };

  const handleClickSearch = () => {
    if (searchTerm) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }
    fetchSharesTitles(searchTerm);
    setSearchTerm('');
  };

  return isLoading ? (
    <CustomLoading />
  ) : (
    <Page>
      <PageListHeader
        subTitle={t('TitleRegistryBook')}
        description={t('SocietyLastSharesTitleNumber', {
          number: getLastTitleNumber(actualSociety, sharesTitles),
        })}
        actions={[<GoBackButton />]}
      />

      <SearchModule
        placeholder={formatSearchPlaceholder(
          [t('Number'), t('Partner'), t('Email'), t('CIF')],
          i18n
        )}
        buttonText={searchTerm ? t('SearchTitle') : t('CancelSearch')}
        handleChangeSearchTerm={handleChangeSearchTerm}
        handleClickSearch={handleClickSearch}
      />
      <SharesTitlePageBody sharesTitles={sharesTitles} />

      {!isSearching && (
        <DataTablePagination
          itemPerPage={rowsPerPage}
          totalItems={actualSociety?.sharesTitlesCount?.total}
          paginate={handleChangePage}
          currentPage={currentPage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          customItemPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </Page>
  );
}

export default TitleRegistryBook;
